import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, Mail } from "lucide-react";
import SEO from "../components/SEO";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Terms of Service"
        description="RNB Consulting Group's terms of service. Review our policies regarding use of our website, services, payments, and intellectual property."
        keywords="RNB Consulting terms of service, healthcare consulting terms, service agreement"
        noIndex={false}
      />
      
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-rnb-light to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-rnb-blue hover:text-rnb-navy mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-rnb-navy mb-4">
            Terms of Service
          </h1>
          <p className="text-rnb-gray">
            <strong>Effective Date:</strong> January 10, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none">
            <p className="text-rnb-gray leading-relaxed mb-8">
              These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of the RNB Consulting Group 
              website, client portal, and all related services (collectively, the &ldquo;Services&rdquo;). By accessing 
              or using the Services, you agree to be bound by these Terms.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              1. Eligibility & Scope
            </h2>
            <p className="text-rnb-gray mb-4">
              The Services are intended for use by potential clients and partners, as well as authorized users 
              of the client portal. You must be at least 18 years of age to use the Services.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              2. Account Registration & Security
            </h2>
            <p className="text-rnb-gray mb-4">
              Some features require account creation. You agree to provide accurate, current information and 
              maintain the confidentiality of your credentials. You are responsible for all activity under your account.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              3. Permitted & Prohibited Uses
            </h2>
            <p className="text-rnb-gray mb-4">
              You agree to use the Services only for lawful purposes. Prohibited uses include, but are not limited to:
            </p>
            <ul className="list-disc pl-6 text-rnb-gray mb-4 space-y-2">
              <li>Violating any applicable laws or regulations</li>
              <li>Attempting to gain unauthorized access to any portion of the Services or systems</li>
              <li>Reverse engineering, decompiling, or modifying any part of the Services</li>
              <li>Uploading malicious code, spam, or infringing content</li>
              <li>Using the Services for competitive analysis or to build a competing product</li>
            </ul>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              4. Payments, Refunds & Cancellations
            </h2>
            <p className="text-rnb-gray mb-4">
              Payments for programs and services may be processed through the Services. All sales are final unless otherwise stated:
            </p>
            <ul className="list-disc pl-6 text-rnb-gray mb-4 space-y-2">
              <li><strong>Foundations First™:</strong> No refunds after the first consultation call.</li>
              <li><strong>CareRevive Initiative™ & Concierge Frontline Experience™:</strong> No cancellations or refunds after the intake form is processed.</li>
            </ul>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              5. Intellectual Property
            </h2>
            <p className="text-rnb-gray mb-4">
              All content, materials, downloads, and software on the Services are owned by RNB Consulting Group 
              or its licensors. You may not copy, distribute, modify, or create derivative works without express written consent.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              6. Third-Party Links & Integrations
            </h2>
            <p className="text-rnb-gray mb-4">
              The Services may contain links to or integrate with third-party websites or tools. RNB Consulting Group 
              is not responsible for third-party content, privacy practices, or any resulting damages.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              7. Disclaimers
            </h2>
            <p className="text-rnb-gray mb-4">
              The Services and all content are provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without warranties of any kind, 
              express or implied. RNB Consulting Group does <strong>not</strong> provide medical, legal, or accounting advice. 
              Use of the Services is at your own risk.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              8. Limitation of Liability
            </h2>
            <p className="text-rnb-gray mb-4">
              To the maximum extent permitted by law, RNB Consulting Group and its affiliates shall not be liable 
              for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or 
              revenues, arising from your use of the Services.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              9. Indemnification
            </h2>
            <p className="text-rnb-gray mb-4">
              You agree to indemnify and hold harmless RNB Consulting Group and its affiliates, officers, and employees 
              from any claims, liabilities, damages, losses, or expenses (including legal fees) arising out of your 
              violation of these Terms or misuse of the Services.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              10. Modifications & Termination
            </h2>
            <p className="text-rnb-gray mb-4">
              RNB Consulting Group reserves the right to modify these Terms at any time. Changes will be posted on this 
              page with a new effective date. Continued use of the Services after changes constitutes acceptance. We may 
              suspend or terminate your access to the Services at any time for violation of these Terms or for any reason.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              11. Dispute Resolution
            </h2>
            <p className="text-rnb-gray mb-4">
              Any disputes arising from these Terms or the Services shall be resolved first through good faith negotiation 
              and, if unresolved, by binding arbitration or mediation in the State of Georgia, USA.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              12. Force Majeure
            </h2>
            <p className="text-rnb-gray mb-4">
              RNB Consulting Group shall not be liable for any failure or delay in performance due to causes beyond its 
              reasonable control, including but not limited to acts of God, natural disasters, war, or internet outages.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              13. Governing Law
            </h2>
            <p className="text-rnb-gray mb-4">
              These Terms are governed by the laws of the State of Georgia, USA, without regard to conflict of law principles.
            </p>

            <h2 className="font-heading text-2xl font-bold text-rnb-navy mt-12 mb-4">
              14. Contact
            </h2>
            <p className="text-rnb-gray mb-4">
              For questions about these Terms, contact us:
            </p>
            <div className="bg-slate-50 rounded-lg p-6 mt-4">
              <p className="font-semibold text-rnb-navy mb-2">RNB Consulting Group</p>
              <p className="text-rnb-gray">Atlanta, GA, USA</p>
              <p className="text-rnb-gray">
                Email:{" "}
                <a href="mailto:info@rnbconsultinggroup.com" className="text-rnb-blue hover:underline">
                  info@rnbconsultinggroup.com
                </a>
              </p>
              <p className="text-rnb-gray">
                Phone:{" "}
                <a href="tel:888-250-4605" className="text-rnb-blue hover:underline">
                  (888) 250-4605
                </a>
              </p>
            </div>

            <div className="mt-12 pt-8 border-t text-center">
              <p className="text-rnb-gray mb-4">
                By using the Services, you acknowledge and agree to these Terms of Service.
              </p>
              <Link to="/contact">
                <Button className="btn-hover-lift">
                  <Mail className="mr-2 h-4 w-4" />
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
